"use client"

import type { VariantProps } from "class-variance-authority"
import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cva } from "class-variance-authority"

import { cn } from "@productlane/lib/src/browser"

export type TabsVariants = "default" | "transparent" | "light"
export const Tabs = React.memo(TabsPrimitive.Root)

const tabsListVariants = cva(
	"grid justify-left w-full grid-flow-col items-center",
	{
		variants: {
			variant: {
				default: "w-auto gap-1.5 bg-transparent",
				transparent:
					"items-start w-auto overflow-x-scroll md:overflow-x-hidden no-scrollbar",
				light: "w-auto gap-1.5 bg-transparent",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
)

export const TabsList = React.memo(
	React.forwardRef<
		React.ElementRef<typeof TabsPrimitive.List>,
		React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> &
			VariantProps<typeof tabsListVariants>
	>(({ className, children, variant, ...props }, ref) => (
		<TabsPrimitive.List
			ref={ref}
			className={cn(tabsListVariants({ variant, className }))}
			{...props}
		>
			{children}
		</TabsPrimitive.List>
	)),
)

const tabsTriggerPill =
	"h-7 justify-center whitespace-nowrap rounded-full px-2.5 text-secondary shadow-sm ring-1 ring-black/[0.08] transition-[color,background-color,box-shadow] duration-150 ease-[ease] hover:bg-gray-50 hover:text-primary data-[state=active]:bg-gray-100 data-[state=active]:text-primary data-[state=active]:ring-black/[0.14] dark:ring-white/[0.08] dark:hover:bg-gray-800/40 dark:data-[state=active]:bg-gray-800 dark:data-[state=active]:ring-white/[0.14]"

const tabsTriggerVariants = cva(
	"group relative inline-flex w-auto items-center px-3 py-1 text-xs font-medium disabled:pointer-events-none h-full",
	{
		variants: {
			variant: {
				default: tabsTriggerPill,
				transparent:
					"mr-0 flex flex-row items-center gap-1.5 whitespace-nowrap !pl-0 text-left text-gray-450 hover:text-secondary data-[state=active]:text-primary lg:mr-4 dark:text-gray-450 dark:hover:text-secondary dark:data-[state=active]:text-primary",
				light: tabsTriggerPill,
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
)

const tabsTriggerIndicatorVariants = cva(
	"absolute inset-0 -mb-[1px] rounded-[0.45rem] h-full w-full",
	{
		variants: {
			variant: {
				default: "hidden",
				transparent: "",
				light: "hidden",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
)

export const TabsTrigger = React.memo(
	React.forwardRef<
		React.ElementRef<typeof TabsPrimitive.Trigger>,
		React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
			VariantProps<typeof tabsTriggerVariants> & {
				count?: number | string
				countClassName?: string
				indicatorClassName?: string
			}
	>(
		(
			{
				className,
				indicatorClassName,
				children,
				variant,
				count,
				countClassName,
				...props
			},
			ref,
		) => {
			return (
				<TabsPrimitive.Trigger
					className={cn(tabsTriggerVariants({ variant, className }))}
					{...props}
					ref={ref}
				>
					<div
						className={cn(
							"absolute inset-0 w-full",
							tabsTriggerIndicatorVariants({ variant }),
							"hidden group-data-[state=active]:block",
							indicatorClassName,
						)}
					/>
					<span className="relative z-1">{children}</span>
					{count !== undefined &&
						count !== null &&
						count !== 0 &&
						count != "" &&
						count != "0" && (
							<div
								className={cn(
									"relative z-1 ml-1.5 text-xs font-normal tabular-nums text-tertiary opacity-70 group-data-[state=active]:text-secondary group-data-[state=active]:opacity-100",
									countClassName,
								)}
							>
								{count}
							</div>
						)}
				</TabsPrimitive.Trigger>
			)
		},
	),
)

export const TabsContent = React.memo(
	React.forwardRef<
		React.ElementRef<typeof TabsPrimitive.Content>,
		React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
	>(({ className, ...props }, ref) => (
		<TabsPrimitive.Content
			className={cn("focus-visible:outline-none", className)}
			{...props}
			ref={ref}
		/>
	)),
)
